import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Chip,
  Stack,
  Paper,
  Divider,
  IconButton,
  Collapse,
} from "@mui/material";
import {
  Assignment as WorkOrderIcon,
  CheckCircle as CheckIcon,
  Schedule as VendorIcon,
  Warning as IssueIcon,
  ExpandMore as ExpandMoreIcon,
  WbSunny as MorningIcon,
  LunchDining as NoonIcon,
  WbTwilight as AfternoonIcon,
  NightsStay as EveningIcon,
  EventAvailable as FirstThingIcon,
  Summarize as EODIcon,
} from "@mui/icons-material";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { firestore } from "../firebase";
import PropertyContext from "../contexts/PropertyContext";
import { propertyCollection, propertyDoc } from "../utils/firestorePaths";
import { useNavigate } from "react-router-dom";
import WarrantyWidget from "./WarrantyWidget";
import OccupancyHistoryWidget from "./OccupancyHistoryWidget";

export default function Dashboard({ property, userInfo }) {
  const [workOrdersLoading, setWorkOrdersLoading] = useState(true);
  const [taskTodayLoading, setTasksTodayLoading] = useState(true);
  const [vendorsTodayLoading, setVendorsTodayLoading] = useState(true);
  const [vendorTasksLoading, setVendorTasksLoading] = useState(true);

  const [openWorkOrders, setOpenWorkOrders] = useState([]);
  const [pendingWorkOrders, setPendingWorkOrders] = useState([]);
  const [vendorsToday, setVendorsToday] = useState([]);
  const [openIssues, setOpenIssues] = useState([]);
  const [mytasks, setmytasks] = useState([]);

  const [expandedSection, setExpandedSection] = useState(null);

  const { selectedProperty, selectedClient } = useContext(PropertyContext);
  const navigate = useNavigate();

  let firstRun = 0;

  const getWorkOrders = () => {
    const workOrdersRef = propertyCollection(
      selectedClient,
      selectedProperty,
      "workOrders"
    );
    const q2 = query(workOrdersRef);

    const unsub = onSnapshot(q2, (snap) => {
      const openOrders = snap.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        .filter((request) => request.completed === false)
        .filter((request) => request.markedCompleted !== true);

      const pendingOrders = snap.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        .filter((request) => request.completed === false)
        .filter((request) => request.markedCompleted === true);

      setOpenWorkOrders(openOrders);
      setPendingWorkOrders(pendingOrders);
      setWorkOrdersLoading(false);
    });
    return unsub;
  };

  const pushTasksToFirestore = async (tasks) => {
    const batch = writeBatch(firestore);
    const getLocalDateString = () => {
      const today = new Date();
      const localDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
      );
      return localDate.toISOString().split("T")[0];
    };
    const todaysTasksRef = propertyCollection(
      selectedClient,
      selectedProperty,
      "modTasks",
      "dailyReports",
      getLocalDateString()
    );

    const queryTodaysTasks = query(todaysTasksRef);
    const todaysTasksSnapshot = await getDocs(queryTodaysTasks);

    if (todaysTasksSnapshot.empty) {
      tasks.forEach((task) => {
        const docRef = doc(todaysTasksRef);
        batch.set(docRef, task);
      });
      await batch.commit();
      const mySanity = {
        isComplete: true,
        init: true,
      };
      const myDocRef = doc(todaysTasksRef, "sanity");
      await setDoc(myDocRef, mySanity);
    }
  };

  const getTasks = async () => {
    const getLocalDateString = () => {
      const today = new Date();
      const localDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
      );
      return localDate.toISOString().split("T")[0];
    };

    const tasksRef = propertyCollection(
      selectedClient,
      selectedProperty,
      "modTasks",
      "base",
      "allTasks"
    );
    const todaysTasksRef = propertyCollection(
      selectedClient,
      selectedProperty,
      "modTasks",
      "dailyReports",
      getLocalDateString()
    );

    const queryTasks = query(tasksRef);
    const queryTodaysTasks = query(todaysTasksRef);

    const todaysTasksSnapshot = await getDocs(queryTodaysTasks);
    const tasksSnapshot = await getDocs(queryTasks);

    const sanityChecker = async () => {
      const sanityDocument = doc(todaysTasksRef, "sanity");
      const sanityDocCheck = await getDoc(sanityDocument);

      if (sanityDocCheck.exists()) {
        return true;
      } else {
        return false;
      }
    };

    const todaysTasks = [];

    if (todaysTasksSnapshot.empty) {
      sanityChecker().then((exists) => {
        if (exists) {
          console.log("sanity checker override");
        } else {
          if (!firstRun === 0) {
            return console.log("sanity checker override");
          }
          firstRun++;
          console.log("sanity checker found nothing");
        }
      });

      tasksSnapshot.forEach((doc) => {
        const task = doc.data();
        const dayOfWeekLong = new Date(getLocalDateString())
          .toLocaleString("en-US", { weekday: "long" })
          .toLowerCase();
        const dateOfMonth = new Date(getLocalDateString()).getDate();

        if (task.type === "daily") {
          todaysTasks.push(task);
        } else if (
          task.type === "weekly" &&
          task.days.includes(dayOfWeekLong)
        ) {
          todaysTasks.push(task);
        } else if (
          task.type === "monthly" &&
          task.days.includes(dateOfMonth.toString())
        ) {
          todaysTasks.push(task);
        }
      });
      await pushTasksToFirestore(todaysTasks);
    } else {
      todaysTasksSnapshot.forEach((doc) => {
        const task = doc.data();
        if (!task.isComplete) {
          task.docId = doc.id;
          todaysTasks.push(task);
        }
      });
    }
    setmytasks(todaysTasks);
    setTasksTodayLoading(false);
  };

  const getVendors = () => {
    const vendorRef = propertyCollection(
      selectedClient,
      selectedProperty,
      "Vendors"
    );
    const queryVendors = query(vendorRef);

    const unsub = onSnapshot(queryVendors, (snap) => {
      const todaysVendors = [];
      snap.docs.forEach((doc) => {
        const vendor = {
          id: doc.id,
          ...doc.data(),
        };

        const today = new Date();
        const dayOfWeek = today.toLocaleString("en-us", { weekday: "long" });
        const dateOfMonth = today.getDate();

        if (
          vendor.type === "weekly" &&
          vendor.days &&
          vendor.days.includes(dayOfWeek)
        ) {
          todaysVendors.push(vendor);
        }

        if (
          vendor.type === "monthly" &&
          vendor.days &&
          vendor.days.includes(dateOfMonth.toString())
        ) {
          todaysVendors.push(vendor);
        }
      });
      setVendorsToday(todaysVendors);
      setVendorsTodayLoading(false);
    });
    return unsub;
  };

  const getPropertyIssues = () => {
    const issuesRef = propertyCollection(
      selectedClient,
      selectedProperty,
      "propertyIssues"
    );
    const queryIssues = query(issuesRef);

    const unsub = onSnapshot(queryIssues, (snap) => {
      const openIssues = snap.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        .filter((request) => request.completed === false);

      setOpenIssues(openIssues);
      setVendorTasksLoading(false);
    });
    return unsub;
  };

  useEffect(() => {
    if (!selectedProperty || !selectedClient) return;

    // Reset loading state so cards show a spinner while the new property loads
    setWorkOrdersLoading(true);
    setTasksTodayLoading(true);
    setVendorsTodayLoading(true);
    setVendorTasksLoading(true);

    const unsubWorkOrders = getWorkOrders();
    const unsubVendors = getVendors();
    const unsubPropertyIssues = getPropertyIssues();
    getTasks();

    return () => {
      unsubWorkOrders && unsubWorkOrders();
      unsubVendors && unsubVendors();
      unsubPropertyIssues && unsubPropertyIssues();
    };
  }, [selectedProperty, selectedClient]);

  const handleMarkComplete = async (task) => {
    const time = new Date().toLocaleTimeString("en-US", {
      hour: "numeric",
      hour12: true,
      minute: "numeric",
    });
    const initials = userInfo.fullName
      .split(" ")
      .map((n) => n[0])
      .join(".")
      .concat(".");

    const getLocalDateString = () => {
      const today = new Date();
      const localDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
      );
      return localDate.toISOString().split("T")[0];
    };

    const docRef = propertyDoc(
      selectedClient,
      selectedProperty,
      "modTasks",
      "dailyReports",
      getLocalDateString(),
      task.docId
    );
    const updateData = {
      markedCompletedBy: initials,
      markedCompletedTime: time,
      isComplete: true,
    };
    await updateDoc(docRef, updateData);
    const newTasks = mytasks.filter((item) => item.docId !== task.docId);
    setmytasks(newTasks);
  };

  const handleToggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  // Group tasks by time
  const tasksByTime = {
    "first thing": mytasks.filter((task) => task.time === "first thing"),
    morning: mytasks.filter((task) => task.time === "morning"),
    noon: mytasks.filter((task) => task.time === "noon"),
    afternoon: mytasks.filter((task) => task.time === "afternoon"),
    evening: mytasks.filter((task) => task.time === "evening"),
    EOD: mytasks.filter((task) => task.time === "EOD"),
  };

  const getTimeIcon = (time) => {
    switch (time) {
      case "first thing":
        return FirstThingIcon;
      case "morning":
        return MorningIcon;
      case "noon":
        return NoonIcon;
      case "afternoon":
        return AfternoonIcon;
      case "evening":
        return EveningIcon;
      case "EOD":
        return EODIcon;
      default:
        return CheckIcon;
    }
  };

  const getTimeLabel = (time) => {
    switch (time) {
      case "first thing":
        return "Opening Tasks";
      case "morning":
        return "Morning Tasks";
      case "noon":
        return "Noon Tasks";
      case "afternoon":
        return "Afternoon Tasks";
      case "evening":
        return "Evening Tasks";
      case "EOD":
        return "End of Day Tasks";
      default:
        return time;
    }
  };

  // Summary Card Component
  const SummaryCard = ({ title, count, icon: Icon, color, loading, linkTo }) => (
    <Card
      sx={{
        cursor: "pointer",
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: 6,
        },
      }}
      onClick={() => linkTo && navigate(linkTo)}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            {loading ? (
              <CircularProgress size={32} />
            ) : (
              <Typography variant="h3" fontWeight="700" color={color}>
                {count}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              bgcolor: `${color}.100`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon sx={{ fontSize: 32, color: color }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  // Task Section Component
  const TaskSection = ({ time, tasks, icon: Icon }) => {
    const isExpanded = expandedSection === time;

    return (
      <Paper sx={{ mb: 2 }}>
        <Box
          sx={{
            p: 2,
            cursor: "pointer",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            "&:hover": {
              bgcolor: "action.hover",
            },
          }}
          onClick={() => handleToggleSection(time)}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Icon color="primary" />
            <Typography variant="h6" fontWeight="600">
              {getTimeLabel(time)}
            </Typography>
            <Chip label={`${tasks.length} tasks`} size="small" color="primary" />
          </Box>
          <IconButton
            sx={{
              transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.3s",
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>
        <Collapse in={isExpanded}>
          <Divider />
          <Box sx={{ p: 2 }}>
            <Stack spacing={2}>
              {tasks.map((task, index) => (
                <Box
                  key={task.docId || index}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                    bgcolor: "background.default",
                    borderRadius: 1,
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  <Typography variant="body1">{task.taskDetails}</Typography>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<CheckIcon />}
                    onClick={() => handleMarkComplete(task)}
                  >
                    Complete
                  </Button>
                </Box>
              ))}
            </Stack>
          </Box>
        </Collapse>
      </Paper>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" fontWeight="600" gutterBottom>
          Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Welcome back, {userInfo?.fullName || "User"}
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Open Work Orders"
            count={openWorkOrders.length}
            icon={WorkOrderIcon}
            color="primary"
            loading={workOrdersLoading}
            linkTo="/workOrders"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Tasks Remaining"
            count={mytasks.length}
            icon={CheckIcon}
            color="warning"
            loading={taskTodayLoading}
            linkTo="/mod-tasks"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Vendors Today"
            count={vendorsToday.length}
            icon={VendorIcon}
            color="info"
            loading={vendorsTodayLoading}
            linkTo="/vendors"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Property Issues"
            count={openIssues.length}
            icon={IssueIcon}
            color="error"
            loading={vendorTasksLoading}
            linkTo="/propertyIssues"
          />
        </Grid>
      </Grid>

      {/* Widgets Row - Show for Managers and Maintenance */}
      {(userInfo?.userType === "manager" || userInfo?.userType === "maintenance") && (
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={6} lg={8}>
            <OccupancyHistoryWidget />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <WarrantyWidget selectedProperty={selectedProperty} clientId={selectedClient} />
          </Grid>
        </Grid>
      )}

      {/* Tasks Section */}
      <Box>
        <Box mb={3}>
          <Typography variant="h5" fontWeight="600" gutterBottom>
            Today's Tasks
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Complete your daily tasks organized by time of day
          </Typography>
        </Box>

        {taskTodayLoading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : mytasks.length === 0 ? (
          <Paper sx={{ p: 8, textAlign: "center" }}>
            <CheckIcon sx={{ fontSize: 64, color: "success.main", mb: 2 }} />
            <Typography variant="h5" fontWeight="600" gutterBottom>
              All Tasks Complete!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Great job! You've finished all your tasks for today.
            </Typography>
          </Paper>
        ) : (
          <Box>
            {Object.entries(tasksByTime).map(([time, tasks]) =>
              tasks.length > 0 ? (
                <TaskSection
                  key={time}
                  time={time}
                  tasks={tasks}
                  icon={getTimeIcon(time)}
                />
              ) : null
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
